import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ValidationQueueDetail } from 'src/app/compliance/validation-queue/details/models/validation-queue-detail';
import { ValidationQueueFile } from 'src/app/compliance/validation-queue/details/models/validation-queue-file';
import { ValidationQueueGridLookups } from 'src/app/compliance/validation-queue/grid/models/validation-queue-grid-lookups';
import { DocumentAttribute } from 'src/app/shared/lib/compliance-shared/document-attributes-form/document-attributes-form.store';
import { FetchArgs, PagingToken, PagingTokenFixed } from 'src/app/shared/models/core';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { FeatureFlag } from '../../../shared/models/enums/feature-flag';
import { ComplianceValidationStatusTypeEnum } from '../../compliance-report/enums/compliance-validation-status-type.enum';
import { AIVendorFeedbackFromCL } from '../../models/validation-status.model';
import { ValidationQueueBypassedDetail } from '../../validation-queue/details/models/validation-queue-bypassed-detail';
import { ValidationQueueGridItem } from '../../validation-queue/grid/models/validation-queue-grid-item';
import { ValidationQueueService } from '../../validation-queue/services/validation-queue.service';
import { ComplianceValidationQueueClient } from '../compliance-validation-queue-client.service';
import { FetchArguments2OfValidationQueueGridFilter } from '../models/fetch-arguments2of-validation-queue-grid-filter.model';
import { KeyValueDto } from '../models/key-value-dto.model';
import { LookupResponseResponse } from '../models/lookup-response-response.model';
import { MetadataValueDto } from '../models/metadata-value-dto.model';
import { PagingTokenOfValidationQueueBypassedDto } from '../models/paging-token-of-validation-queue-bypassed-dto.model';
import { PagingTokenOfValidationQueueGridItemDto } from '../models/paging-token-of-validation-queue-grid-item-dto.model';
import { SendAIVendorFeedbackFromCLDto } from '../models/send-aivendor-feedback-from-cldto.model';
import { ValidationQueueDetailDto } from '../models/validation-queue-detail-dto.model';
import { ValidationQueueFileDto } from '../models/validation-queue-file-dto.model';
import { ValidationQueueFileStatusUpdateDto } from '../models/validation-queue-file-status-update-dto.model';
import { NSwagMigrationService } from './nswag-migration.service';

// 11 endpoints
@Injectable({ providedIn: 'root' })
export class ValidationQueueNSwagMigrationService extends NSwagMigrationService {
    protected readonly className = 'ValidationQueueNSwagMigrationService';
    protected readonly featureFlag = FeatureFlag.NSwagMigrationValidationQueueService;
    private readonly legacy = inject(ValidationQueueService);
    private readonly complianceValidationQueueClient = inject(ComplianceValidationQueueClient);

    // ** ComplianceValidationQueueController.GetGridLookupDataAsync
    getGridLookups(): Observable<ValidationQueueGridLookups | LookupResponseResponse> {
        return this.invoke(x => x
            ? this.legacy.getGridLookups()
            : this.complianceValidationQueueClient.getGridLookupData());
    }

    // ** ComplianceValidationQueueController.GetDeclineCategoryLookups
    getDeclineCategoryLookups(): Observable<KeyValue<number, string>[] | KeyValueDto[]> {
        return this.invoke(x => x
            ? this.legacy.getDeclineCategoryLookups()
            : this.complianceValidationQueueClient.getDeclineCategoryLookups());
    }

    // ** ComplianceValidationQueueController.GetByContractId
    getByContractId(contractId: number): Observable<ValidationQueueDetail | ValidationQueueDetailDto> {
        return this.invoke(x => x
            ? this.legacy.getByContractId(contractId)
            : this.complianceValidationQueueClient.getByContractId(contractId));
    }

    // ** ComplianceValidationQueueController.GetBypassedDocsByContractId
    getBypassedDocs(contractId: number): Observable<PagingToken<ValidationQueueBypassedDetail[]> | PagingTokenOfValidationQueueBypassedDto> {
        return this.invoke(x => x
            ? this.legacy.getBypassedDocs(contractId)
            : this.complianceValidationQueueClient.getBypassedDocsByContractId(contractId));
    }

    // ** ComplianceValidationQueueController.GetBypassedDocsCountByContractId
    getBypassedDocsCount(contractId: number): Observable<number> {
        return this.invoke(x => x
            ? this.legacy.getBypassedDocsCount(contractId)
            : this.complianceValidationQueueClient.getBypassedDocsCountByContractId(contractId));
    }

    // ** ComplianceValidationQueueController.MarkDocsAsViewed
    sendBypassedDocumentsToMarkAsViewed(docIds: number[]): Observable<any | boolean> {
        return this.invoke(x => x
            ? this.legacy.sendBypassedDocumentsToMarkAsViewed(docIds)
            : this.complianceValidationQueueClient.markDocsAsViewed(docIds));
    }

    // ** ComplianceValidationQueueController.GetFileById
    getFileById(contractId: number, id: number): Observable<ValidationQueueFile | ValidationQueueFileDto> {
        return this.invoke(x => x
            ? this.legacy.getFileById(contractId, id)
            : this.complianceValidationQueueClient.getFileById(id, contractId.toString()));
    }

    // ** ComplianceValidationQueueController.GetHospitalSystems
    getHospitalSystems(filter: string, take: number): Observable<KeyValue<number, string>[] | KeyValueDto[]> {
        return this.invoke(x => x
            ? this.legacy.getHospitalSystems(filter, take)
            : this.complianceValidationQueueClient.getHospitalSystems(filter, take));
    }

    // ** ComplianceValidationQueueController.SearchPostAsync
    // searchPost(fetchArguments: FetchArguments2OfValidationQueueGridFilter): Observable<PagingTokenOfValidationQueueGridItemDto> {
    searchValidationQueue(args: FetchArgs | FetchArguments2OfValidationQueueGridFilter): Observable<PagingTokenFixed<ValidationQueueGridItem[]> | PagingTokenOfValidationQueueGridItemDto> {
        return this.invoke(x => x
            ? this.legacy.searchValidationQueue(args as FetchArgs)
            : this.complianceValidationQueueClient.searchPost(args as FetchArguments2OfValidationQueueGridFilter));
    }

    // ** ComplianceValidationQueueController.SetValidationQueueFileStatus
    setValidationQueueFileStatus(
        id: number,
        contractId: number,
        statusId: number,
        candidateDocumentId?: number,
        documentTypeId?: number,
        expirationDateUtc?: Date,
        documentAttributes?: DocumentAttribute[] | MetadataValueDto[],
        rejectionNote?: string,
        sendEmailToCS?: boolean,
        unacceptDocument?: boolean,
        rejectionCategory?: KeyValue<number, string> | KeyValueDto): Observable<Object | ValidationQueueFileStatusUpdateDto> {
        const isDeclined = statusId === ComplianceValidationStatusTypeEnum.Declined;
        return this.invoke(x => x

            ? this.legacy.setValidationQueueFileStatus(
                id,
                contractId,
                statusId,
                candidateDocumentId,
                documentTypeId,
                expirationDateUtc,
                documentAttributes as DocumentAttribute[],
                rejectionNote,
                sendEmailToCS,
                unacceptDocument,
                rejectionCategory as KeyValue<number, string>)

            : this.complianceValidationQueueClient.setValidationQueueFileStatus(contractId, id, {
                candidateDocumentId,
                documentTypeId,
                rejectionNote: isDeclined ? rejectionNote : undefined,
                rejectionCategory: isDeclined ? rejectionCategory as KeyValueDto : undefined,
                sendEmailToCredentialSpecialist: isDeclined ? sendEmailToCS : undefined,
                unacceptDocument: isDeclined ? unacceptDocument : undefined,
                statusId,
                expirationDateUtc,
                documentAttributes: documentAttributes as MetadataValueDto[]
            }));
    }

    // ** ComplianceValidationQueueController.SendAIVendorFeedback
    sendAIVendorFeedback(feedback: AIVendorFeedbackFromCL | SendAIVendorFeedbackFromCLDto): Observable<unknown | void> {
        return this.invoke(x => x
            ? this.legacy.sendAIVendorFeedback(feedback)
            : this.complianceValidationQueueClient.sendAIVendorFeedback(feedback));
    }
}
